<script setup>
import { useRoute } from "vue-router"
import { useCustomSwal } from "@/composables/usePopup"

import RecruiterService from "@/services/RecruiterService"
import FooterBlank from "@/layouts/components/FooterBlank.vue"

definePage({
  meta: {
    layout: "blank",
    public: true,
  },
})
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR

const { showCustomSwal } = useCustomSwal()
const route = useRoute()
const childRef = ref(null)

const info = ref({})
const loading = ref(false)
const errorData = ref(null)

const onSubmit = async (obj) => {
  try {
    loading.value = true
    const response = await RecruiterService.registerRecruiter({
      ...obj,
    })

    if (response) {
      await showCustomSwal({
        title: $gettext("Données enregistrées avec succès"),
        html: `
        <p class="mb-0" style="font-size: 16px; font-style: normal">${$gettext(
          "Un email contenant un lien de participation vous a été envoyé."
        )}</p>`,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
        timer: 10000,
        timerProgressBar: true,
        footer: `
        <p class="mb-0" style="font-size: 14px">
          ${$gettext("Si vous ne le recevez pas : consultez vos spams.")}
          </p>
          <p class="mb-3" style="font-size: 14px">${$gettext(
            "Si vous n'avez rien dans vos spams,"
          )}</p>
          <p class="mb-1" style="font-size: 14px"><strong>${$gettext(
            "NE VOUS RÉINSCRIVEZ PAS"
          )}</strong> ${$gettext("et contactez-nous par e-mail à : ")}
            <a class="text-justify text-sky" href='mailto:classements.france@eduniversal-group.com' target="_blank">
              classements.france@eduniversal-group.com
            </a>
          </p>
          `,
      })
    }
  } catch (error) {
    errorData.value = error?.data
    if (errorData.value?.code === "LIMIT_RECRUITER_REGISTERED") {
      await showCustomSwal({
        html: `
            <p class=" text-danger fw-bold">
            ${$gettext("Le nombre maximum d'inscription pour ce programme a été atteint. Si vous pensez que c'est une erreur vous pouvez contacter notre support à l'adresse .")}
            <a href="mailto:classements.france@eduniversal-group.com" class="fw-bold text-primary">
                  classements.france@eduniversal-group.com
              </a>
          </p>
            `,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      })
    } else if (errorData.value?.message === "already_participated") {
      await showCustomSwal({
        title: $gettext(
          "Vous êtes déjà inscrit pour répondre à l'enquête Eduniversal."
        ),
        html: `
          <p style="font-size: 16px">
            ${$gettext(
              "Nous venons de vous renvoyer le mail de participation (pensez à vérifier vos spams)."
            )}
          </p>
          <p style="font-size: 16px">
            ${$gettext(
              "Si vous ne recevez pas le mail, contactez notre support à l'adresse "
            )}
            <a class="text-justify text-primary" href='mailto:classements.france@eduniversal-group.com' target="_blank" >classements.france@eduniversal-group.com</a>
          </p>
      `,
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonColor: "#d33",
        cancelButtonText: $gettext("FERMER"),
      })
    } else if (errorData.value?.message === "already_completed") {
      await showCustomSwal({
        title: $gettext("Vous avez déjà répondu à l'enquête Eduniversal"),
        html: `
          <p style="font-size: 16px">
            ${$gettext(
              "Nous venons de vous renvoyer le mail de confirmation (pensez à vérifier vos spams)."
            )}
          </p>
          <p style="font-size: 16px">
            ${$gettext(
              "Si vous ne recevez pas le mail, contactez notre support à l'adresse "
            )}
            <a class="text-justify text-primary" href='mailto:classements.france@eduniversal-group.com' target="_blank" >classements.france@eduniversal-group.com</a>
          </p>
      `,
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonColor: "#d33",
        cancelButtonText: $gettext("FERMER"),
      })
    }
  } finally {
    childRef.value?.resetInput()
    loading.value = false
  }
}

onMounted(async () => {
  const { params } = route
  try {
    const res = await RecruiterService.getRegisterRecruiter(params.id)
    info.value = res || {}
  } catch (error) {
    errorData.value = error?.data
  }
})
</script>

<template>
  <div class="register-student">
    <div class="register-student__container" style="max-width: 900px">
      <div class="register-student__bg">
        <img src="@/assets/images/palm_bg.svg" alt="palm" />
      </div>
      <!-- Start Header -->
      <header
        class="d-flex align-center justify-space-between register-student__container-header"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img
            src="@/assets/logo-eduniversal.png"
            alt="Logo"
            style="max-width: 140px"
          />
        </RouterLink>
        <div></div>
        <!-- <BtnLang @onChange="onChangeLang" /> -->
      </header>
      <main
        style="background: #fff; padding: 0 40px"
        :class="[
          'register-student__main py-6',
          errorData?.code ? 'd-flex align-center flex-column' : '',
        ]"
      >
        <ContentError v-if="errorData?.code" :code="errorData?.code" />
        <template v-else>
          <div>
            <h1 v-if="info?.program?.type === 2">
              {{ $gettext("Enquêtes") }} {{ $gettext("Eduniversal") }} {{ CURRENT_YEAR }}
              <div style="font-size: 1.85rem; color: #cb9f6e">
                {{ $gettext("des") }} {{ $gettext("Meilleurs Licences, Bachelors") }}
                {{ $gettext("et") }} {{ $gettext("Grandes Écoles") }}
              </div>
            </h1>
            <h1 v-else>
              {{ $gettext("Enquêtes") }} {{ $gettext("Eduniversal") }} {{ CURRENT_YEAR }}
              <div style="font-size: 1.85rem; color: #cb9f6e">
                {{ $gettext("des") }} {{ $gettext("Meilleurs Masters, MS") }}
                {{ $gettext("et") }} {{ $gettext("MBA") }}
              </div>
            </h1>
          </div>

          <hr class="my-4" />

          <FormRegisterRecruiter
            :data="info"
            :loading="loading"
            @update:modelValue="onSubmit($event)"
            ref="childRef"
            class="mt-4"
          />
        </template>
      </main>
    </div>
    <VCard class="register-student__contact">
      <ContactWithUs />
    </VCard>
    <FooterBlank :footerRh="true" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/pages/register-student.scss";

.swal2-title {
  font-size: 16px;
  font-weight: 700;
}
</style>
